import { track } from './track';
import { isChunkLoadError } from './chunk-load-error';

// 印度网络环境下的慢请求阈值（4G/5G 为主，适当收严）
const SLOW_RESOURCE_THRESHOLD_MS = 2000;
const SLOW_API_THRESHOLD_MS = 1000;
// 每类最多上报 5 条，避免单字段过长
const SLOW_REPORT_MAX_ITEMS = 5;
// 只统计真正的静态资源，排除 beacon（Sentry 上报）、iframe 等噪音
const STATIC_RESOURCE_TYPES = new Set(['script', 'link', 'img', 'css', 'other']);

function getRttFromNavigationTiming(): number {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (!nav) return 0;

  // TCP 握手时间，最接近真实 RTT
  const tcpRtt = nav.connectEnd - nav.connectStart;
  if (tcpRtt > 0) return Math.round(tcpRtt);

  // HTTP/2 连接复用时 connectStart === connectEnd，用 TTFB 兜底
  // TTFB 包含服务端处理时间，会偏大，但至少是真实测量值
  const ttfb = nav.responseStart - nav.requestStart;
  if (ttfb > 0) return Math.round(ttfb);

  return 0;
}

/**
 * 读取当前网络质量信息（Network Information API，无需授权）
 * Safari / Firefox 不支持，不可用时各字段返回空/0
 */
export function getNetworkInfo(): {
  network_effective_type: string;
  network_rtt: number;
  network_downlink: number;
  save_data: boolean;
} {
  const conn = (navigator as any).connection;
  const rtt = getRttFromNavigationTiming();
  console.log(`rtt data: ${conn?.rtt}, ${rtt}`);

  return {
    network_effective_type: conn?.effectiveType ?? '',
    network_rtt: conn?.rtt ?? rtt,
    network_downlink: conn?.downlink ?? 0,
    save_data: conn?.saveData ?? false,
  };
}

/**
 * 监听首屏 LCP，上报 page_visible 事件
 * 仅在初次加载（hard navigation）时有效，SPA 路由切换不触发
 */
export function setupLcpTracking(): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  let fired = false;
  try {
    const observer = new PerformanceObserver(() => {
      if (fired) return;
      fired = true;
      observer.disconnect();
      track('page_visible');
    });
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    // 部分旧版 WebView 不支持此类型，静默忽略
  }
}

/**
 * 观测 FCP / LCP / CLS，在合适时机上报 page_status_report
 * 方案 C：visibilitychange → hidden 或 load+5s，先到者触发（防重复上报）
 */
export function setupPageStatusReport(): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  let fcp: number | null = null;
  let lcpValue: number | null = null;
  let cls = 0;
  let reported = false;
  const observers: PerformanceObserver[] = [];

  function report() {
    if (reported) return;
    reported = true;
    observers.forEach(o => { try { o.disconnect(); } catch { /* ignore */ } });

    // TTFB：从 navigation start 到收到第一字节，含 DNS + TCP + SSL + 服务器等待
    // 目标 < 800ms；印度用户访问欧美服务器时通常 > 1000ms
    let ttfb: number | null = null;
    try {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        const nav = navEntries[0] as PerformanceNavigationTiming;
        if (nav.responseStart > 0) {
          ttfb = Math.round(nav.responseStart);
        }
      }
    } catch { /* ignore */ }

    track('page_status_report', {
      fcp,
      lcp: lcpValue,
      cls: parseFloat(cls.toFixed(4)),
      ttfb,
      ...getNetworkInfo(),
    });
  }

  try {
    const fcpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          fcp = Math.round(entry.startTime);
        }
      }
    });
    fcpObserver.observe({ type: 'paint', buffered: true });
    observers.push(fcpObserver);
  } catch { /* ignore */ }

  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      if (entries.length) {
        lcpValue = Math.round(entries[entries.length - 1].startTime);
      }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    observers.push(lcpObserver);
  } catch { /* ignore */ }

  try {
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const shift = entry as PerformanceEntry & { hadRecentInput: boolean; value: number; };
        if (!shift.hadRecentInput) {
          cls += shift.value;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });
    observers.push(clsObserver);
  } catch { /* ignore */ }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') report();
  });

  // useEffect 在 hydration 后执行，load 事件可能已触发，需检查 readyState
  if (document.readyState === 'complete') {
    setTimeout(report, 5000);
  } else {
    window.addEventListener('load', () => {
      setTimeout(report, 5000);
    }, { once: true });
  }
}

/**
 * 从 Error 对象提取 js_runtime_error 事件属性
 *
 * @param error - 错误对象（接受 unknown 以防 throw 非 Error 值）
 * @param currentPath - 当前路由路径；不传时 fallback 到 window.location.pathname
 */
export function extractErrorTrackInfo(error: unknown, currentPath?: string): {
  message: string;
  error_name: string;
  source_file: string;
  line_no: number;
  current_path: string;
  is_chunk_load_error: boolean;
  chunk_id: string;
  chunk_fail_reason: string;
  chunk_url: string;
} {
  const resolvedPath = currentPath ?? (typeof window !== 'undefined' ? window.location.pathname : '');

  // 防御：throw 了非 Error 值（字符串、对象等）
  if (!(error instanceof Error)) {
    return {
      message: String(error).slice(0, 250),
      error_name: typeof error,
      source_file: '',
      line_no: 0,
      current_path: resolvedPath,
      is_chunk_load_error: false,
      chunk_id: '',
      chunk_fail_reason: '',
      chunk_url: '',
    };
  }

  const message = (error.message || '').slice(0, 250);
  const stack = error.stack || '';
  let source_file = '';
  let line_no = 0;

  // 跳过第一行（"Error: message"），从第二行开始找第一个有效 stack frame
  for (const line of stack.split('\n').slice(1)) {
    const match = line.match(/\(?(.+?\.(?:js|ts|tsx|jsx|mjs)):(\d+):\d+\)?/);
    if (match) {
      source_file = match[1];
      line_no = parseInt(match[2], 10);
      break;
    }
  }

  const chunkError = isChunkLoadError(error);
  let chunk_id = '';
  let chunk_fail_reason = '';
  let chunk_url = '';

  if (chunkError) {
    // ChunkLoadError 的 source_file 固定是 webpack runtime，无分析价值，跳过
    const msg = error.message || '';

    // 格式1/2: "Loading chunk <id> failed. (<reason>: <url>)"
    // chunk ID 可含 / 和 - (如 app/casino/page、app-page)，用非贪婪匹配
    const withReasonMatch = msg.match(/Loading chunk (.+?) failed\. \((\w+): (.+?)\)\s*$/);
    // 格式3: "Loading chunk <id> failed." —— 有 chunk ID 但无 reason/url
    const noReasonMatch = msg.match(/Loading chunk (.+?) failed\./);
    // 格式4: "Failed to fetch dynamically imported module: <url>" —— 浏览器原生动态 import 失败
    const fetchFailedMatch = msg.match(/Failed to fetch dynamically imported module:\s*(.+?)\s*$/);

    if (withReasonMatch) {
      chunk_id = withReasonMatch[1].trim();
      chunk_fail_reason = withReasonMatch[2];
      chunk_url = withReasonMatch[3].trim();
    } else if (noReasonMatch) {
      chunk_id = noReasonMatch[1].trim();
    } else if (fetchFailedMatch) {
      chunk_fail_reason = 'fetch_failed';
      chunk_url = fetchFailedMatch[1].trim();
    }
  }

  return {
    message,
    error_name: error.name || '',
    source_file: chunkError ? '' : source_file,
    line_no: chunkError ? 0 : line_no,
    current_path: resolvedPath,
    is_chunk_load_error: chunkError,
    chunk_id,
    chunk_fail_reason,
    chunk_url,
  };
}

/**
 * 监听资源加载和 API 请求，超过阈值时汇总上报 page_slow_report
 * - 静态资源（JS/图片/字体等）：> SLOW_RESOURCE_THRESHOLD_MS
 * - API（URL 含 /services/）：> SLOW_API_THRESHOLD_MS
 * - 无慢请求时不发事件
 * - 触发时机：load+5s 或 visibilitychange → hidden，先到者触发
 */
export function setupSlowReportTracking(): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  const slowResources: Array<{ name: string; ms: number; }> = [];
  const slowApis: Array<{ name: string; ms: number; }> = [];
  let reported = false;
  let observer: PerformanceObserver | null = null;

  function report() {
    if (reported) return;
    reported = true;
    try { observer?.disconnect(); } catch { /* ignore */ }

    if (slowResources.length === 0 && slowApis.length === 0) return;

    const fmt = (arr: typeof slowResources) =>
      arr
        .sort((a, b) => b.ms - a.ms)
        .slice(0, SLOW_REPORT_MAX_ITEMS)
        .map(r => `${r.name}:${r.ms}`)
        .join('|') || null;

    track('page_slow_report', {
      slow_resources: fmt(slowResources),
      slow_resource_total: slowResources.length,
      slow_apis: fmt(slowApis),
      slow_api_total: slowApis.length,
      ...getNetworkInfo(),
    });
  }

  try {
    observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const e = entry as PerformanceResourceTiming;
        const ms = Math.round(e.duration);

        if (e.initiatorType === 'xmlhttprequest' || e.initiatorType === 'fetch') {
          if (e.name.includes('/services/') && ms >= SLOW_API_THRESHOLD_MS) {
            try {
              slowApis.push({ name: new URL(e.name).pathname, ms });
            } catch { /* ignore invalid URL */ }
          }
        } else if (STATIC_RESOURCE_TYPES.has(e.initiatorType) && ms >= SLOW_RESOURCE_THRESHOLD_MS) {
          // 只取文件名，去掉 hash query
          const filename = e.name.split('/').pop()?.split('?')[0] ?? e.name;
          slowResources.push({ name: filename, ms });
        }
      }
    });
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    return;
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') report();
  });

  if (document.readyState === 'complete') {
    setTimeout(report, 5000);
  } else {
    window.addEventListener('load', () => setTimeout(report, 5000), { once: true });
  }
}

